<template>
  <vd-error
    v-if="isNotFound"
    status="404"
    message="The rule you are looking for does not exist"
    :back-link="backLink"
  />
  <div v-else class="configuration-rule-form-page">
    <vd-page-form
      class="configuration-rule-form"
      :preview="preview"
      :title="pageTitle"
      :subtitle="(configurationClass && configurationClass.displayName) || ''"
      :back-link="backLink"
      :form="form"
      :form-changed="formChanged"
      display-cancel-alert
      :hide-actions="!inWIPVersion"
      :save-callback="saveForm"
      :save-loading="saveLoading"
      :loading="queryLoading"
      :allow-delete="!!ruleId"
      :rules="formRules"
    >
      <template #actions>
        <VdSwitch v-model="isTimedEvent" active-text="Timed event" />
      </template>

      <h2>Targeted audiences</h2>

      <VdCard>
        <VdFormField prop="segmentId" placeholder="Select a segment" label="Segment" required class="segment-selection">
          <SegmentsSelectInput v-model="form.segmentId" :game-id="gameId" :version-id="versionId" />
        </VdFormField>

        <p>Platforms</p>
        <div class="platform-versions">
          <div class="platform">
            <VdSwitch v-model="form.ios" />
            <div class="name">iOS</div>
            <div v-if="form.ios" class="game-version">
              <VdFormField prop="iosGameVersionFrom" :error="versionErrors.ios" label="Game version:" inline>
                <VdInputVersion v-model="form.iosGameVersionFrom" placeholder="earliest" prefix="from" />
              </VdFormField>
              <VdFormField prop="iosGameVersionTo" inline>
                <VdInputVersion v-model="form.iosGameVersionTo" placeholder="latest" prefix="to" />
              </VdFormField>
            </div>
          </div>

          <div class="platform">
            <VdSwitch v-model="form.android" />
            <div class="name">Android</div>
            <div v-if="form.android" class="game-version">
              <VdFormField
                prop="androidGameVersionFrom"
                placeholder="earliest"
                :error="versionErrors.android"
                label="Game version:"
                inline
              >
                <VdInputVersion v-model="form.androidGameVersionFrom" placeholder="earliest" prefix="from" />
              </VdFormField>
              <VdFormField prop="androidGameVersionTo" inline>
                <VdInputVersion v-model="form.androidGameVersionTo" placeholder="latest" prefix="to" />
              </VdFormField>
            </div>
          </div>
        </div>
      </VdCard>

      <h2 v-if="isTimedEvent">Scheduling</h2>

      <section v-if="isTimedEvent" class="advanced-configuration card">
        <div class="schedule">
          <VdFormField prop="beginsAt" required label="Start - End dates (UTC)">
            <el-date-picker
              :model-value="pickerBeginsAt"
              type="datetime"
              placeholder="Start date and time (UTC)"
              @update:model-value="(value) => onDateChanged('beginsAt', value)"
            />
            <span class="to">to</span>
            <el-date-picker
              :model-value="pickerEndsAt"
              type="datetime"
              placeholder="End date and time (UTC)"
              @update:model-value="(value) => onDateChanged('endsAt', value)"
            />
          </VdFormField>
        </div>
      </section>

      <div v-if="configurationClass" class="attributes-section">
        <h2>Attributes</h2>
      </div>
      <section v-if="configurationClass && isClassDefault">
        <component
          :is="configurationClass.isMultiInstance ? 'el-collapse' : 'div'"
          v-model="expandedInstances"
          accordion
        >
          <component
            :is="configurationClass.isMultiInstance ? 'el-collapse-item' : 'div'"
            v-for="(instance, index) in dynamicInstances.filter(({ enabled }) => enabled)"
            :key="`instance-${index}`"
            :title="configurationClass.isMultiInstance ? undefined : instance.name"
            :name="instance.id"
          >
            <template v-if="configurationClass.isMultiInstance" #title>
              <div>
                <span>{{ buildInstanceName(index + 1) }}</span>
                <span
                  v-if="configurationClass?.dynamicInstances"
                  class="delete-button"
                  @click.stop="onDeleteInstance(instance.id)"
                >
                  <i class="pi pi-trash"></i>
                </span>
              </div>
            </template>
            <ConfigurationRuleAttributeValues
              :instance-id="instance.id"
              :class-instances="dynamicInstances"
              :class-attributes="classAttributes"
              :current-rule="currentRule"
              :other-rules="!configurationClass?.dynamicInstances ? otherRules : []"
              :display-default-values="!configurationClass?.dynamicInstances"
              @attribute-value-changed="(val) => setAttribute(instance.id, val)"
            />
          </component>
        </component>
      </section>
      <vd-button
        v-if="configurationClass?.dynamicInstances"
        type="secondary"
        skin="blue"
        icon-left="fa:add"
        class="add-instance-button"
        @click="onAddInstance"
      >
        Add instance
      </vd-button>
      <section v-if="configurationClass && isShopClass">
        <ShopConfigurationRuleAttribute
          :configuration-class="configurationClass"
          :available-shops="availableShops"
          @attribute-value-changed="(val) => setAttribute(dynamicInstances[0].id, val)"
        />
      </section>
    </vd-page-form>
  </div>
</template>

<script lang="ts">
/**
 * This page displays a configuration rule form.
 */

import { VdSwitch, VdFormField, VdInputVersion, VdCard, FormRule } from "@voodoo.io/voodoo-ui";
import compareVersions from "compare-versions";
import { parseJSON } from "date-fns";
import { defineComponent, computed, reactive, watch, ref, nextTick } from "vue";
import { useRouter } from "vue-router";
import { v4 as uuidv4 } from "uuid";

import InstancesFilter from "@/components/InstancesFilter/InstancesFilter.vue";
import SegmentsSelectInput from "@/components/SegmentsSelectInput/SegmentsSelectInput.vue";
import {
  useMutationResponse,
  AttributeValue,
  ConfigurationRuleFormInput,
  useConfigurationRuleFormPageQuery,
  useConfigurationRuleFormPageSaveMutation,
  ConfigurationRuleFormPageConfigurationClassInstanceFragment,
  ClassInstance,
} from "@/graphql";
import useFormTracking from "@/hooks/form-tracking";
import links from "@/router/links";

import ConfigurationRuleAttributeValues from "./components/ConfigurationRuleAttributeValues.vue";
import ShopConfigurationRuleAttribute from "./components/ShopConfigurationRuleAttribute.vue";
import useShopsManager from "@/modules/Store/hooks/useShopsManager";

export default defineComponent({
  name: "ConfigurationRuleFormPage",

  components: {
    SegmentsSelectInput,
    ConfigurationRuleAttributeValues,
    ShopConfigurationRuleAttribute,
    InstancesFilter,
    VdSwitch,
    VdFormField,
    VdInputVersion,
    VdCard,
  },

  props: {
    gameId: {
      type: String,
      required: true,
    },
    versionId: {
      type: String,
      required: true,
    },
    classId: {
      type: String,
      required: true,
    },

    /**
     * If the ruleId is present, update the configuration rule, create it otherwise.
     */
    ruleId: {
      type: String,
      default: null,
    },

    /**
     * If the instanceId is present, update the instance, create it otherwise.
     */
    instanceId: {
      type: String,
      default: null,
    },

    /**
     * If true, the form is a creation form with the value taken from the ruleId's rule
     */
    duplicate: {
      type: Boolean,
      default: false,
    },

    /**
     * If true, enter in the preview mode
     */
    preview: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const router = useRouter();
    const isFormReadOnly = ref(props.preview);
    const isTimedEvent = ref(false);

    // Separate refs for the pickers so they aren't bound to the UTC ISO strings in form,
    // which would cause a feedback loop (picker shows local time → triggers change → converts to UTC → picker re-renders shifted)
    const pickerBeginsAt = ref<Date | null>(null);
    const pickerEndsAt = ref<Date | null>(null);

    /**
     * Define the form
     */
    const form = reactive<ConfigurationRuleFormInput & { beginsAt?: string; endsAt?: string }>({
      segmentId: "",
      ios: true,
      iosGameVersionFrom: "",
      iosGameVersionTo: "",
      android: true,
      androidGameVersionFrom: "",
      androidGameVersionTo: "",
      beginsAt: undefined,
      endsAt: undefined,
      instances: [],
    });

    /**
     * Enable form tracking
     */
    const { formChanged, resetTracking: resetFormTracking } = useFormTracking(form);

    /**
     * Form validation rules
     * Validate version format to avoid error when comparing with tmp incorrect values
     */

    const formRules: Record<string, FormRule[]> = {
      segmentId: [
        {
          type: "string",
          required: true,
          message: "the segment is required",
        },
      ],
      beginsAt: [
        {
          type: "string",
          required: true,
          message: "Start and end dates are required",
        },
        {
          type: "string",
          validator(_: unknown, value: string, callback: (error?: Error) => void) {
            if (value && !form.endsAt) {
              return callback(new Error("End date is required."));
            }
            if (value && form.endsAt) {
              const beginsAt = parseJSON(value);
              const endsAt = parseJSON(form.endsAt);

              if (beginsAt >= endsAt) {
                return callback(new Error("The start date must be earlier than the end date."));
              }
            }
            return callback();
          },
        },
      ],
    };

    const versionErrors = reactive({
      ios: "",
      android: "",
    });

    const validateVersion = (scope: "ios" | "android") => {
      const fromVersion = form[`${scope}GameVersionFrom` as never];
      const toVersion = form[`${scope}GameVersionTo` as never];
      if (fromVersion && toVersion && compareVersions.validate(fromVersion) && compareVersions.validate(toVersion)) {
        if (!compareVersions.compare(fromVersion, toVersion, "<=")) {
          versionErrors[scope] = "The first version should be lower or equal than the second.";
          return;
        }
      }
      versionErrors[scope] = "";
    };

    watch(form, () => {
      validateVersion("ios");
      validateVersion("android");
    });

    const utcIsoToLocalDisplayDate = (isoString: string): Date => {
      const d = new Date(isoString);
      return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes());
    };

    const onDateChanged = (fieldName: "beginsAt" | "endsAt", value?: Date | null) => {
      // Store the raw Date in the picker ref so it doesn't loop back through the UTC conversion
      if (fieldName === "beginsAt") {
        pickerBeginsAt.value = value ?? null;
      } else {
        pickerEndsAt.value = value ?? null;
      }

      if (!value) {
        form[fieldName] = undefined;
        return;
      }
      // Treat the user's selection as UTC (picker has no timezone awareness)
      form[fieldName] = new Date(
        Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), value.getHours(), value.getMinutes())
      ).toISOString();
    };
    /**
     * Shop Query data
     */

    const { shopsToDisplay: availableShops } = useShopsManager(props.gameId, props.versionId);
    /**
     * Query data
     */
    const { result: queryResult, loading: queryLoading } = useConfigurationRuleFormPageQuery({
      gameId: props.gameId,
      versionId: props.versionId,
      classId: props.classId,
    });

    const inWIPVersion = computed(() => queryResult.value?.game?.version?.isWip || false);

    const configurationClass = computed(() => queryResult.value?.game?.version?.configurationClass);

    const classAttributes = computed(() => queryResult.value?.game?.version?.configurationClass?.attributes ?? []);

    const classInstances = computed(() => queryResult.value?.game?.version?.configurationClass?.instances ?? []);

    const configurationRules = computed(() => configurationClass.value?.rules || []);

    const isClassDefault = computed(() => configurationClass.value && configurationClass.value?.type === "DEFAULT");
    const isShopClass = computed(() => configurationClass.value && configurationClass.value?.type === "SHOP");

    const currentRule = computed(() => {
      if (props.ruleId) {
        return configurationRules.value.find(({ id }) => id === props.ruleId);
      }
      return undefined;
    });

    const segmentName = computed(() => (currentRule.value && currentRule.value.segment.name) || "");

    const otherRules = computed(() => {
      if (configurationClass.value) {
        return configurationRules.value.filter(({ id }) => id !== props.ruleId);
      }
      return [];
    });

    const isNotFound = computed(() => props.ruleId && !queryLoading.value && !currentRule.value);

    /**
     * Filter class instances
     */
    const expandedInstances = ref<string[]>([]);
    const dynamicInstances = ref<ConfigurationRuleFormPageConfigurationClassInstanceFragment[]>([]);

    watch(queryResult, (watchedResult) => {
      const watchedClass = watchedResult?.game?.version?.configurationClass;

      const baseInstances = watchedClass?.instances ?? [];

      const ruleInstances =
        (watchedClass?.rules?.find(({ id }) => id === props.ruleId)?.instances as ClassInstance[]) ?? [];

      dynamicInstances.value = watchedClass?.dynamicInstances ? ruleInstances : baseInstances;

      expandedInstances.value = dynamicInstances.value.map(({ id }) => id).slice(0, 1);
    });

    const buildInstanceName = (number: number) => `Instance ${number}`;

    const onAddInstance = async () => {
      const id = uuidv4();

      const newInstance = {
        id,
        enabled: true,
        values: classAttributes.value.map(({ technicalName, isArrayType, formFieldType }) => {
          let value: string | boolean | string[] = isArrayType ? [] : "";
          value = formFieldType === "boolean" ? false : value;
          return {
            technicalName,
            value,
          };
        }),
      };

      dynamicInstances.value = [
        ...dynamicInstances.value,
        { ...newInstance, name: buildInstanceName(dynamicInstances.value.length + 1) },
      ];

      form.instances = [...form.instances, newInstance];

      expandedInstances.value = expandedInstances.value.concat(id);

      await nextTick();

      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        });
      }, 50);
    };

    const onDeleteInstance = (instanceId: string) => {
      form.instances = form.instances
        .map((instance) => {
          if (instance.id === instanceId) {
            return {
              ...instance,
              enabled: false,
            };
          }
          return instance;
        })
        .filter(({ enabled }) => enabled);

      const deletedInstance = dynamicInstances.value.find(({ id }) => id === instanceId);

      if (deletedInstance) {
        dynamicInstances.value = [...dynamicInstances.value.filter(({ id }) => id !== instanceId)];
      }
    };

    /**
     * Fill the form
     */
    watch(queryLoading, (loading) => {
      if (loading) {
        return;
      }

      if (!configurationClass.value) {
        return;
      }

      form.instances = dynamicInstances.value.map(({ id, values, enabled }) => ({
        id,
        enabled,
        values: values.map(({ technicalName, defaultValue }) => ({ technicalName, value: defaultValue })),
      }));

      if (currentRule.value) {
        const { platform, segment, schedule } = currentRule.value;

        const currentRuleInstancesValues: Record<string, Record<string, AttributeValue>> = Object.fromEntries(
          currentRule.value.instances.map(({ id, values }) => {
            const recordValues = Object.fromEntries(values.map(({ technicalName, value }) => [technicalName, value]));
            return [id, recordValues];
          })
        );

        form.segmentId = segment.id;
        form.ios = platform.ios;
        if (form.ios) {
          form.iosGameVersionFrom = platform.iosGameVersionFrom;
          form.iosGameVersionTo = platform.iosGameVersionTo;
        }

        form.android = platform.android;
        if (form.android) {
          form.androidGameVersionFrom = platform.androidGameVersionFrom;
          form.androidGameVersionTo = platform.androidGameVersionTo;
        }

        if (schedule && schedule.ranges.length > 0) {
          isTimedEvent.value = true;
          form.beginsAt = schedule.ranges[0].beginsAt;
          form.endsAt = schedule.ranges[0].endsAt;
          // Populate the picker refs with local Dates that visually show the UTC time
          pickerBeginsAt.value = utcIsoToLocalDisplayDate(schedule.ranges[0].beginsAt);
          pickerEndsAt.value = utcIsoToLocalDisplayDate(schedule.ranges[0].endsAt);
        }

        form.instances = form.instances.map((instance) => ({
          ...instance,
          values: instance.values.map(({ technicalName, value }) => {
            const instanceValues = currentRuleInstancesValues[instance.id];
            let instanceValue = value;
            if (instanceValues) {
              instanceValue = instanceValues[technicalName];
            }
            return { technicalName, value: instanceValue };
          }),
        }));
      }

      /** We want start tracking changes after loading the data  */
      resetFormTracking();
    });

    /**
     * Update the form page title
     */
    const pageTitle = computed(() => {
      if (props.ruleId) {
        const title = (currentRule.value && segmentName.value) || "";
        if (props.duplicate) {
          return `Duplicate of ${title}`;
        }
        return title;
      }
      return "New configuration rule";
    });

    /**
     * Helpers
     */

    const setAttribute = (
      instanceId: string,
      { technicalName, value: newValue }: { technicalName: string; value: AttributeValue }
    ) => {
      form.instances = form.instances.map((instance) => {
        if (instance.id === instanceId) {
          return {
            ...instance,
            values: instance.values.map((attrValue) =>
              attrValue.technicalName === technicalName ? { technicalName, value: newValue } : attrValue
            ),
          };
        }
        return instance;
      });
    };

    /**
     * Define the location to return after save/deletion/cancel
     */

    const backLink = { name: links.games.configurations.list };

    /**
     * Save the configuration rule
     */
    const { mutate: save, loading: saveLoading } = useConfigurationRuleFormPageSaveMutation({});

    const saveForm = async () => {
      const response = await save({
        gameId: props.gameId,
        versionId: props.versionId,
        classId: props.classId,
        configurationRuleId: props.duplicate ? null : props.ruleId, // if we are in duplication mode, we don't want to pass the id
        input: {
          segmentId: form.segmentId,
          ios: form.ios,
          iosGameVersionFrom: form.iosGameVersionFrom,
          iosGameVersionTo: form.iosGameVersionTo,
          android: form.android,
          androidGameVersionFrom: form.androidGameVersionFrom,
          androidGameVersionTo: form.androidGameVersionTo,
          beginsAt: isTimedEvent.value ? form.beginsAt : null,
          endsAt: isTimedEvent.value ? form.endsAt : null,
          instances: form.instances.filter(({ enabled }) => enabled),
        },
      });

      const data = useMutationResponse(response, (resp) => resp?.saveConfigurationRule);
      if (data?.success) {
        router.push({ ...backLink, hash: `#${props.classId}` });
      }
    };

    /**
     * Stretch the textarea to fill the entire cell height
     */
    watch(queryLoading, (loading) => {
      if (!loading) {
        setTimeout(() => {
          const rows = document.querySelectorAll(".el-table__fixed .el-table__row");
          rows.forEach((row) => {
            const rowHeight = row.querySelector("td")?.offsetHeight || 0;
            const textarea = row.querySelector("textarea");
            if (textarea && rowHeight > 100) {
              textarea.style.height = `${rowHeight - 30}px`;
            }
          });
        }, 50);
      }
    });

    return {
      queryResult,
      pageTitle,
      backLink,
      form,
      dynamicInstances,
      currentRule,
      classInstances,
      classAttributes,
      isFormReadOnly,
      saveForm,
      queryLoading,
      saveLoading,
      configurationClass,
      otherRules,
      setAttribute,
      expandedInstances,
      formRules,
      versionErrors,
      isNotFound,
      inWIPVersion,
      formChanged,
      onDateChanged,
      pickerBeginsAt,
      pickerEndsAt,
      isTimedEvent,
      isClassDefault,
      isShopClass,
      availableShops,
      onAddInstance,
      onDeleteInstance,
      buildInstanceName,
    };
  },
});
</script>

<style lang="scss">
.configuration-rule-form-page {
  .vd-form {
    .vd-form__content {
      display: grid;
      row-gap: 30px;
    }
  }

  .configuration-rule-form {
    /* Avoiding issues with el-table when expanded */
    max-width: 100%;
    overflow-x: scroll;
  }

  .target-audiences {
    .segment-selection {
      margin-bottom: size("L");
    }
  }

  .card {
    @extend %card;
    padding: size("L");
  }

  .attributes-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h3 {
    font-weight: 600;
    margin-bottom: size("L");
  }

  .rule-segment-name {
    color: $slate;
  }

  .platform-game-versions-label-component {
    color: $grey;
  }

  .attribute-tags {
    @extend %tags;
    margin-top: size("S");
    justify-content: flex-start;
    max-height: 100%;
    height: 100%;
  }

  .add-instance-button {
    display: flex;
    justify-self: end;
  }

  .delete-button {
    color: black;
    padding-left: 2rem;
  }

  .delete-button:hover {
    color: red;
  }

  .platform-versions {
    .platform {
      display: flex;
      align-items: center;
      margin: size("M") 0;
      line-height: 50px;

      .name {
        min-width: 70px;
        margin-left: size("M");
        font-size: font("M");
      }

      .game-version {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        column-gap: 10px;
        width: 100%;
        animation: appear 0.4s ease;

        .to {
          margin: 0 size("M");
          font-size: font("M");
        }
      }
    }
  }

  .schedule {
    .to {
      margin: 0 size("M");
      font-size: font("M");
    }
  }
}
</style>
